import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Clock,
  ArrowRight,
  TrendingUp,
  Brain,
  Users,
  Zap,
  Target,
  LineChart,
  ArrowUpRight,
} from "lucide-react";

import {
  TechLabel,
  StatusBadge,
  PageContainer,
  motionFadeUp,
  C,
} from "../../design-system";

const blogPosts = [
  {
    title: "The future of AI in recruitment: 2025 trends",
    excerpt: "Predictive analytics, LLMs, and automation reshaping every touchpoint of hiring.",
    category: "AI & Technology",
    date: "Jan 20, 2025",
    readTime: "8 min",
    author: "Priya Sharma",
    icon: Brain,
    accent: C.coral,
    featured: true,
  },
  {
    title: "Why unified HR platforms beat point solutions",
    excerpt: "The case for consolidating ATS, CRM and HRMS into one record.",
    category: "Product",
    date: "Jan 18, 2025",
    readTime: "6 min",
    author: "David Chen",
    icon: Target,
    accent: C.mint,
  },
  {
    title: "Reducing hiring bias with AI screening",
    excerpt: "Data-driven hiring for more diverse, more accountable teams.",
    category: "Best Practices",
    date: "Jan 15, 2025",
    readTime: "7 min",
    author: "Sarah Johnson",
    icon: Users,
    accent: C.gold,
  },
  {
    title: "10 metrics every recruiter should track",
    excerpt: "Time-to-hire, quality-of-hire, fill-rate what to measure and how.",
    category: "Analytics",
    date: "Jan 12, 2025",
    readTime: "5 min",
    author: "Michael Park",
    icon: LineChart,
    accent: C.coral,
  },
  {
    title: "How to write job descriptions that convert",
    excerpt: "AI-assisted JD writing for SEO, inclusivity and click-through.",
    category: "Hiring Tips",
    date: "Jan 10, 2025",
    readTime: "6 min",
    author: "Emma Wilson",
    icon: Zap,
    accent: C.mint,
  },
  {
    title: "The ROI of recruitment technology",
    excerpt: "Real numbers how AI cuts cost-per-hire by 50% while raising quality.",
    category: "ROI",
    date: "Jan 08, 2025",
    readTime: "9 min",
    author: "James Thompson",
    icon: TrendingUp,
    accent: C.gold,
  },
];

const categories = ["All", ...Array.from(new Set(blogPosts.map((p) => p.category)))];

export default function Blog() {
  const [selected, setSelected] = useState("All");
  const filtered = useMemo(
    () => (selected === "All" ? blogPosts : blogPosts.filter((p) => p.category === selected)),
    [selected]
  );
  const featured = blogPosts.find((p) => p.featured) || blogPosts[0];

  return (
    <div
      className="relative"
      style={{
        background: C.paper,
        color: C.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HERO */}
      <section className="relative pt-16 pb-16 md:pt-24 md:pb-20 border-b ds-bg-spotlight" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <motion.div initial="hidden" animate="show" variants={motionFadeUp}>
            <motion.div variants={motionFadeUp} custom={0}>
              <StatusBadge>Blog · {blogPosts.length} posts</StatusBadge>
            </motion.div>
            <motion.h1
              variants={motionFadeUp}
              custom={1}
              className="mt-5 text-5xl sm:text-6xl lg:text-[5rem] xl:text-[5.75rem] font-extrabold leading-[0.92] tracking-tight max-w-4xl"
              style={{ color: C.forest }}
            >
              Notes from the
              <br />
              <span style={{ color: `${C.forest}80` }}>hiring world.</span>
            </motion.h1>
            <motion.p
              variants={motionFadeUp}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-2xl"
              style={{ color: C.grid }}
            >
              Field-tested writing on AI in recruitment, the metrics that matter,
              and what we're shipping next. Short, useful, no fluff.
            </motion.p>
          </motion.div>
        </PageContainer>
      </section>

      {/* FEATURED POST */}
     <div
  className="inline-flex flex-wrap items-center gap-px mb-10"
  style={{ background: C.hairline }}
>
        <PageContainer>
          <div className="mb-8">
            <TechLabel style={{ color: `${C.grid}99` }}>featured · latest</TechLabel>
          </div>

          <motion.article
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="ds-spatial relative grid md:grid-cols-12 gap-0 cursor-pointer group"
            style={{ background: C.paper, border: `1px solid ${C.hairline}`, borderLeft: `4px solid ${featured.accent}` }}
          >
            {/* Visual block, geometric stand-in for hero image */}
            <div
              className="md:col-span-5 relative aspect-[4/3] md:aspect-auto p-8 flex items-center justify-center overflow-hidden"
              style={{ background: C.forest }}
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -top-10 -right-10 w-44 h-44 opacity-30 blur-3xl"
                style={{ background: featured.accent }}
              />
              <div className="relative">
                <div
                  className="w-24 h-24 flex items-center justify-center"
                  style={{ background: featured.accent, color: C.forest }}
                >
                  <featured.icon className="w-10 h-10" strokeWidth={1.6} />
                </div>
              </div>
              <span className="absolute top-4 left-4">
                <TechLabel style={{ color: featured.accent }}>{featured.id}</TechLabel>
              </span>
              <span className="absolute bottom-4 right-4">
                <TechLabel style={{ color: "rgba(247,247,245,0.55)" }}>fig · feature</TechLabel>
              </span>
            </div>

            {/* Body */}
            <div className="md:col-span-7 p-8 md:p-10 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <TechLabel style={{ color: featured.accent }}>{featured.category}</TechLabel>
                  <span className="w-px h-3" style={{ background: C.hairline }} />
                  <TechLabel style={{ color: `${C.grid}aa` }}>{featured.date}</TechLabel>
                  <span className="w-px h-3" style={{ background: C.hairline }} />
                  <TechLabel style={{ color: `${C.grid}aa` }}>
                    <Clock className="w-3 h-3 inline mr-1" />
                    {featured.readTime}
                  </TechLabel>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold leading-tight tracking-tight transition-colors group-hover:text-[#1A3C2B]" style={{ color: C.forest }}>
                  {featured.title}
                </h2>
                <p className="mt-4 text-base md:text-lg leading-relaxed" style={{ color: C.grid }}>
                  {featured.excerpt}
                </p>
              </div>
              <div className="mt-8 flex items-center justify-between pt-6 border-t" style={{ borderColor: C.hairline }}>
                <TechLabel style={{ color: `${C.grid}99` }}>by · {featured.author}</TechLabel>
                <span className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: C.forest }}>
                  Read article
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </div>
          </motion.article>
        </PageContainer>
      </div>

      {/* CATEGORY FILTER + GRID */}
      <section className="py-16 md:py-20 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <StatusBadge>All posts</StatusBadge>
              <h2 className="mt-4 text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: C.forest }}>
                Browse the archive.
              </h2>
            </div>
            <TechLabel style={{ color: `${C.grid}99` }}>
              {filtered.length} of {blogPosts.length} shown
            </TechLabel>
          </div>

          {/* Tabs */}
          <div className="inline-flex flex-wrap items-center gap-px mb-10" style={{ background: C.hairline }}>
            {categories.map((cat) => {
              const active = cat === selected;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setSelected(cat)}
                  className="cursor-pointer px-4 md:px-5 py-3 text-xs font-bold uppercase transition-colors duration-150 inline-flex items-center gap-2"
                  style={{
                    letterSpacing: "0.18em",
                    background: active ? C.forest : C.paper,
                    color: active ? C.paper : C.grid,
                  }}
                >
                  <span className="w-1.5 h-1.5" style={{ background: active ? C.mint : `${C.grid}77` }} />
                  {cat}
                </button>
              );
            })}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: C.hairline }}>
            {filtered.map((post, i) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="ds-spatial relative p-7 md:p-8 cursor-pointer group transition-colors duration-200 hover:bg-white"
                style={{ background: C.paper, borderLeft: `4px solid ${post.accent}` }}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 flex items-center justify-center" style={{ background: C.forest, color: C.paper }}>
                    <post.icon className="w-5 h-5" strokeWidth={1.6} />
                  </div>
                  <TechLabel style={{ color: post.accent }}>{post.id}</TechLabel>
                </div>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <TechLabel style={{ color: post.accent }}>{post.category}</TechLabel>
                </div>
                <h3 className="text-lg font-bold leading-tight transition-colors group-hover:text-[#1A3C2B]" style={{ color: C.forest }}>
                  {post.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: C.grid }}>
                  {post.excerpt}
                </p>
                <div className="mt-6 pt-4 border-t flex items-center justify-between" style={{ borderColor: C.hairline }}>
                  <TechLabel style={{ color: `${C.grid}99` }}>
                    {post.date} · {post.readTime}
                  </TechLabel>
                  <ArrowUpRight
                    className="w-4 h-4 transition-all duration-150 opacity-50 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    style={{ color: post.accent }}
                  />
                </div>
              </motion.article>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* NEWSLETTER STRIP */}
      <section className="py-16 md:py-20" style={{ background: C.forest, color: C.paper }}>
        <PageContainer>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <StatusBadge color={C.mint}>Stay in the loop</StatusBadge>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold leading-[1.05] tracking-tight">
                One post a fortnight. No spam.
              </h2>
            </div>
            <form
              onSubmit={(e) => e.preventDefault()}
              className="flex w-full md:w-auto md:min-w-[420px] border"
              style={{ borderColor: "rgba(158,255,191,0.4)" }}
            >
              <input
                type="email"
                required
                placeholder="you@company.com"
                className="flex-1 bg-transparent px-4 py-3 text-sm focus:outline-none placeholder:text-[rgba(247,247,245,0.4)]"
                style={{ color: C.paper, fontFamily: "inherit" }}
              />
              <button
                type="submit"
                className="ds-skeu-btn-mint px-5 text-xs font-bold uppercase inline-flex items-center"
                style={{ letterSpacing: "0.18em" }}
              >
                Subscribe
              </button>
            </form>
          </div>
        </PageContainer>
      </section>
    </div>
  );
}
